import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useCalendarData } from './CalendarDataContext';
import ReportProblem from './ReportProblem';
import { countyLabel } from '../../utils/countyDisplay';

/**
 * The home page: the next 90 days of sales across every live state.
 *
 * Grouped by month rather than shown as one long list, because the question a
 * subscriber actually arrives with is "what is coming up", and a flat list of
 * ninety days of rows answers it worse than four short ones.
 *
 * Sales known only as a month appear here too, under a "Typically September"
 * heading. Most sales are known as a month long before they are known as a date,
 * and dropping them would make the page look finished while being wrong.
 */

const STATUS_BADGE = {
  confirmed: 'badge-success',
  expected: 'badge-info',
  cancelled: 'badge-danger',
  postponed: 'badge-warning',
  unknown: 'badge-secondary'
};

function SaleRow({ sale }) {
  const { path } = useCalendarData();

  return (
    <div style={{
      padding: '12px 0',
      borderBottom: '1px solid var(--color-border)',
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px',
      alignItems: 'baseline'
    }}>
      <div style={{ flex: '1 1 240px', minWidth: 0 }}>
        <Link
          to={path(`/counties/${sale.countyId}`)}
          style={{ color: 'var(--color-heading)', fontWeight: 700, textDecoration: 'none' }}
        >
          {sale.countyLabel}, {sale.stateCode}
        </Link>
        <div style={{ color: 'var(--color-text-light)', fontSize: '0.9rem' }}>
          {sale.dateLabel}
        </div>
        <div style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>
          {[sale.saleKindLabel, sale.platformLabel].filter(Boolean).join(', ')}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
        <span className={`badge ${STATUS_BADGE[sale.status] || 'badge-secondary'}`}>
          {sale.statusLabel}
        </span>
        {sale.listUrl && (
          <a
            href={sale.listUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-secondary"
            style={{ fontSize: '0.85rem' }}
          >
            View the list
          </a>
        )}
      </div>
    </div>
  );
}

function CalendarHome({ liveStates = [], comingSoonStates = [] }) {
  const { api, path } = useCalendarData();
  const [groups, setGroups] = useState([]);
  const [recentlyPosted, setRecentlyPosted] = useState([]);
  const [emptyStates, setEmptyStates] = useState([]);
  const [options, setOptions] = useState({ saleKinds: [], platforms: [], statuses: [] });
  const [filters, setFilters] = useState({ state: '', saleKind: '', platform: '', status: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const params = new URLSearchParams();
      if (filters.state) params.append('states', filters.state);
      if (filters.saleKind) params.append('saleKinds', filters.saleKind);
      if (filters.platform) params.append('platforms', filters.platform);
      if (filters.status) params.append('statuses', filters.status);

      const data = await api(`/home?${params}`);
      setGroups(data.groups || []);
      setRecentlyPosted(data.recentlyPosted || []);
      setEmptyStates(data.emptyStates || []);
      if (data.options) setOptions(data.options);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters, api]);

  useEffect(() => { load(); }, [load]);

  const selectStyle = {
    padding: '10px',
    fontSize: '16px',
    borderRadius: '6px',
    border: '1px solid var(--color-border)',
    background: 'var(--color-input-bg, var(--color-surface))',
    color: 'var(--color-text)',
    flex: '1 1 140px',
    minWidth: 0
  };

  const total = groups.reduce((sum, group) => sum + group.sales.length, 0);

  return (
    <div>
      <h1 style={{ color: 'var(--color-heading)', fontSize: '1.35rem', marginBottom: '4px' }}>
        The next 90 days
      </h1>
      <p style={{ color: 'var(--color-text-light)', marginTop: 0 }}>
        Every sale we track in {liveStates.length} live state{liveStates.length === 1 ? '' : 's'}.
      </p>

      {recentlyPosted.length > 0 && (
        <section style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: '8px',
          padding: '12px 14px',
          marginBottom: '20px'
        }}>
          <h2 style={{ fontSize: '0.95rem', margin: '0 0 8px', color: 'var(--color-heading)' }}>
            Lists posted recently
          </h2>
          {recentlyPosted.map(item => (
            <div key={item.id} style={{ fontSize: '0.88rem', marginBottom: '6px' }}>
              <Link to={path(`/counties/${item.county_id}`)} style={{ color: 'var(--color-text)', fontWeight: 600 }}>
                {countyLabel(item.county_name)}, {item.state_code}
              </Link>
              {' '}
              <a href={item.list_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary)' }}>
                view the list
              </a>
            </div>
          ))}
        </section>
      )}

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}>
        <select
          style={selectStyle}
          value={filters.state}
          onChange={e => setFilters({ ...filters, state: e.target.value })}
          aria-label="Filter by state"
        >
          <option value="">All states</option>
          {liveStates.map(state => (
            <option key={state.state_code} value={state.state_code}>{state.name}</option>
          ))}
        </select>

        <select
          style={selectStyle}
          value={filters.saleKind}
          onChange={e => setFilters({ ...filters, saleKind: e.target.value })}
          aria-label="Filter by sale kind"
        >
          <option value="">All sale kinds</option>
          {options.saleKinds.map(kind => (
            <option key={kind.value} value={kind.value}>{kind.label}</option>
          ))}
        </select>

        <select
          style={selectStyle}
          value={filters.platform}
          onChange={e => setFilters({ ...filters, platform: e.target.value })}
          aria-label="Filter by platform"
        >
          <option value="">All platforms</option>
          {options.platforms.map(platform => (
            <option key={platform.value} value={platform.value}>{platform.label}</option>
          ))}
        </select>

        <select
          style={selectStyle}
          value={filters.status}
          onChange={e => setFilters({ ...filters, status: e.target.value })}
          aria-label="Filter by status"
        >
          <option value="">Any status</option>
          {options.statuses.map(status => (
            <option key={status.value} value={status.value}>{status.label}</option>
          ))}
        </select>
      </div>

      {error && <p style={{ color: 'var(--color-danger-text, var(--color-text))' }}>{error}</p>}
      {loading && <p style={{ color: 'var(--color-text-muted)' }}>Loading</p>}

      {!loading && total === 0 && (
        <p style={{ color: 'var(--color-text-light)' }}>
          Nothing matches those filters in the next 90 days. Try clearing one of them.
        </p>
      )}

      {groups.map(group => (
        <section key={group.key} style={{ marginBottom: '28px' }}>
          <h2 style={{
            fontSize: '1.05rem',
            color: 'var(--color-heading)',
            borderBottom: '2px solid var(--color-primary)',
            paddingBottom: '6px',
            marginBottom: '4px'
          }}>
            {group.label}
          </h2>
          {group.sales.map(sale => <SaleRow key={sale.id} sale={sale} />)}
        </section>
      ))}

      {/*
        BELOW THE LIST, NOT ABOVE IT.
        A live state with nothing in the window still says so by name, rather
        than contributing nothing and leaving a subscriber to wonder whether the
        state they paid for is broken or simply quiet.

        But it is context for the list, not the headline. Rendered first, a
        subscriber watching several quiet states met a stack of boxes saying
        nothing is happening before reaching the sales they opened the page for,
        and on a phone that pushed the first real sale below the fold.

        One compact line per state, no box each, for the same reason.
      */}
      {!loading && emptyStates.length > 0 && (
        <section style={{ marginTop: '4px', marginBottom: '28px' }}>
          {emptyStates.map(state => (
            <p
              key={state.stateCode}
              style={{
                margin: '0 0 4px',
                color: 'var(--color-text-muted)',
                fontSize: '0.85rem',
                lineHeight: 1.5
              }}
            >
              {state.message}
            </p>
          ))}
        </section>
      )}

      {comingSoonStates.length > 0 && (
        <section style={{ marginTop: '36px' }}>
          <h2 style={{ fontSize: '0.95rem', color: 'var(--color-heading)' }}>Coming soon</h2>
          <p style={{ color: 'var(--color-text-light)', fontSize: '0.88rem' }}>
            We are researching these states now. They will appear here as they go live.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {comingSoonStates.map(state => (
              <Link
                key={state.state_code}
                to={path(`/states/${state.state_code}`)}
                className="badge badge-secondary"
                style={{ textDecoration: 'none' }}
              >
                {state.name}
              </Link>
            ))}
          </div>
        </section>
      )}

      <ReportProblem page="home" />
    </div>
  );
}

export default CalendarHome;
